import _ from 'lodash';
import * as ActionTypes from '../actions/actionTypes';
import {GRAPHS_CONFIG, PRESCRIPTIONS_CONFIG} from '../config/prescriptions';
import {DATA_SETS} from '../Constants';
import {applyModifications, convertToGraphData, tableMetaData} from '../utils/behaviour_models/shared/behaviour_models_utils';
import {addMaxFdiValues} from '../utils/fdi_utils';
import {emptyGraphMap, extractTicks} from '../utils/point_utils';
import {calculateWithinPrescription, VALUES_CONFIG} from '../utils/prescriptions_utils';
import {addDays, lastMidnightDate} from '../utils/time_utils';
import {DEFAULT_WRF} from './point_data_mcarthur_mk5';

export const DEFAULT_PRESCRIPTION = -1;

const _calculateTableData = (meta_data, include_history, short_future, weather_data, variables, modifications) => {
  const now = new Date();
  const start_time = include_history ? null : new Date(lastMidnightDate(now)).getTime();
  const end_time = short_future ? new Date(addDays(lastMidnightDate(now), 2)).getTime() : null;

  const new_table_data = {};

  const modified_weather_data = {};

  addMaxFdiValues(weather_data, variables.wind_reduction_factor);

  _.values(weather_data).forEach(wd => {
    if ((!start_time || wd.sortIndex >= start_time) && (!end_time || wd.sortIndex < end_time)) {
      const mods = modifications[wd.name];
      if (mods) {
        modified_weather_data[wd.name] = applyModifications(wd, mods);
      } else {
        modified_weather_data[wd.name] = wd;
      }
    }
  });

  addMaxFdiValues(modified_weather_data, variables.wind_reduction_factor);

  _.values(weather_data).forEach(wd => {
    if ((!start_time || wd.sortIndex >= start_time) && (!end_time || wd.sortIndex < end_time)) {
      const row = { name: wd.name, sortIndex: wd.sortIndex };
      meta_data.forEach(v => {
        if (v.nonFinal) {
          row[v.key] = wd[v.key];
        } else {
          const result = calculateWithinPrescription(wd, variables, v.key);
          if (typeof result !== 'undefined') {
            row[v.key] = result.na ? undefined :
                         result.pass ? 1 :
                         result.close ? 0.5 :
                         result.under ? -1 : 0;
            if (result.na) {
              row[`${v.key}_tooltip`] = 'Not Applicable';
            } else if (result.tooltip) {
              row[`${v.key}_tooltip`] = result.tooltip;
            }
          }
        }
      });

      const modified_wd = modified_weather_data[wd.name];
      meta_data.forEach(v => {
        const unmodifiedValue = row[v.key];
        let modifiedValue;
        let modifiedTooltip;
        if (v.nonFinal) {
          modifiedValue = modified_wd[v.key];
        } else {
          const modifiedResult = calculateWithinPrescription(modified_wd, variables, v.key);
          if (typeof modifiedResult !== 'undefined') {
            modifiedValue = modifiedResult.na ? null :
                            modifiedResult.pass ? 1 :
                            modifiedResult.close ? 0.5 :
                            modifiedResult.under ? -1 : 0;
            modifiedTooltip = modifiedResult.tooltip;
          }
        }
        if (typeof modifiedValue !== 'undefined' && unmodifiedValue !== modifiedValue) {
          row[`${v.key}_modified`] = modifiedValue;
          if (modifiedTooltip) {
            row[`${v.key}_tooltip`] = modifiedTooltip;
          }
        }
      });

      new_table_data[wd.name] = row;
    }
  });
  return new_table_data;
};

const extractVariables = (state) => {
  return {
    prescription: state.prescription,
    //wind_reduction_factor: state.wind_reduction_factor
    wind_reduction_factor: DEFAULT_WRF
  };
};

const generateTableMetaData = (prescription) => {
  const metaData = tableMetaData(VALUES_CONFIG, DATA_SETS.PRESCRIPTIONS);
  const config = PRESCRIPTIONS_CONFIG[prescription];
  metaData.forEach(md => {
    if (config && config[md.key] && config[md.key].hide) {
      md.hide_in_table = true;
    }
  });
  return metaData;
};

const generateGraphsMetaData = (prescription) => {
  const metaData = [...GRAPHS_CONFIG];
  const config = PRESCRIPTIONS_CONFIG[prescription];
  if (!config) {
    metaData.forEach(graph => {
      graph.hide = true;
    });
  } else {
    metaData.forEach(graph => {
      if (graph.variable) {
        const variable_config = config[graph.variable].graph;
        if (variable_config &&
            (!graph.requireVariable || graph.requireVariable === config[graph.variable].variable)) {
          graph.hide = false;
          graph.maxLine = variable_config.max;
          graph.minLine = variable_config.min;
        } else {
          graph.hide = true;
        }
      } else {
        // Make sure the Failure Variables graph is unhidden
        graph.hide = false;
      }
    });
  }
  return metaData;
};

const initialState = {
  prescription: DEFAULT_PRESCRIPTION,
  table_data: {},
  graph_data: emptyGraphMap(GRAPHS_CONFIG),
  table_meta_data: generateTableMetaData(DEFAULT_PRESCRIPTION),
  graph_meta_data: generateGraphsMetaData(DEFAULT_PRESCRIPTION),
  wind_reduction_factor: DEFAULT_WRF
};

const reducer = (state = initialState, action) => {
  switch (action.type) {
    case ActionTypes.SET_POINT_FUEL_TYPE:
      if (action.wind_reduction_factor) {
        return {
          ...state,
          wind_reduction_factor: action.wind_reduction_factor
        };
      } else {
        return {
          ...state,
          wind_reduction_factor: DEFAULT_WRF
        };
      }

    case ActionTypes.SET_ACTIVE_PRESCRIPTION:
      const p = PRESCRIPTIONS_CONFIG[action.prescriptionId] ? action.prescriptionId : DEFAULT_PRESCRIPTION;

      if (p === state.prescription) {
        return state;
      }

      return {
        ...state,
        prescription: p,
        table_meta_data: generateTableMetaData(p),
        graph_meta_data: generateGraphsMetaData(p)
      };

    case ActionTypes.SET_BEHAVIOUR_VARIABLES:
      if (action.data_set === DATA_SETS.PRESCRIPTIONS) {
        return {
          ...state,
          ...action.variables,
          prescription: action.variables.prescription,
          table_meta_data: generateTableMetaData(action.variables.prescription),
          graph_meta_data: generateGraphsMetaData(action.variables.prescription)
        };
      }
      break;

    case ActionTypes.CALCULATE_PRESCRIPTIONS:
      const variables = extractVariables(state);
      const modifications = action.modifications ? action.modifications.properties.data : {};
      const new_table_data =
        _calculateTableData(VALUES_CONFIG,
          action.include_history,
          action.short_future,
          action.weather_data.table_data,
          variables,
          modifications);
      const new_graph_data = convertToGraphData(GRAPHS_CONFIG, new_table_data);
      const ticks = extractTicks(new_graph_data, GRAPHS_CONFIG[0].id);

      return {
        ...state,
        ticks,
        graph_data: new_graph_data,
        table_data: new_table_data
      };

    default:
      return state;
  }
  return state;
};

export default reducer;



// WEBPACK FOOTER //
// ./src/reducers/point_data_prescriptions.js